import { MutationType, STPageType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../base/base-mutation';
import { Rule } from '../base/mutation-rules';
import { MutationSource } from '../../constants';
import type { IValidAddPageMutationData, IValidRemovePageMutationData } from '../base/valid-mutation-data';
import type { MaybeError } from '../base/base-mutation';
import type { PresentationDataModel } from '../../model/presentation-data-model';

export class AddPageMutation extends BaseMutation<PresentationDataModel, IValidAddPageMutationData> {
    public readonly type: MutationType = MutationType.AddPage;

    public apply(presentation: PresentationDataModel) {
        const { pageType, pageId, shapeTreeId, index } = this.data;
        const parentId = this.getParentIdFromData();
        const pageData = { id: pageId, cSld: {} };

        if (pageType === STPageType.slide) {
            const slide = presentation.store.addSlide({ ...pageData, layoutId: parentId }, index);
            slide.store.createShapeTree(shapeTreeId);
            return;
        }

        if (pageType === STPageType.slideLayout) {
            const layout = presentation.store.addLayout({ ...pageData, masterId: parentId, slideGuideList: [] }, index);
            layout.store.createShapeTree(shapeTreeId);
            return;
        }

        if (pageType === STPageType.slideMaster) {
            const master = presentation.store.addMaster(
                { ...pageData, themeId: parentId, layoutIdList: [], slideGuideList: [] },
                index,
            );
            master.store.createShapeTree(shapeTreeId);
            return;
        }

        if (pageType === STPageType.notes) {
            const slide = presentation.store.getSlideById(parentId);
            assert(slide, 'slide not found');

            slide.store.updateNoteSlideId(pageId);
            const nodeSlide = presentation.store.addNoteSlide({ ...pageData, slideId: parentId });
            nodeSlide.store.createShapeTree(shapeTreeId);
            return;
        }

        if (pageType === STPageType.notesMaster) {
            const notesMaster = presentation.store.addNotesMaster({ ...pageData, themeId: parentId });
            notesMaster.store.createShapeTree(shapeTreeId);
            return;
        }
    }

    public invert(): [IValidRemovePageMutationData] | [] {
        const { pageType, pageId, index } = this.data;
        const parentId = this.getParentIdFromData();
        return [{ type: MutationType.RemovePage, pageType, pageId, parentId, index }];
    }

    public check(presentation: PresentationDataModel, source?: MutationSource): MaybeError {
        if (this.data.pageType === STPageType.notesMaster) {
            return;
        }

        const { pageType, pageId, index } = this.data;
        const parentId = this.getParentIdFromData();

        const err = Rule.model.should.notExist(presentation, pageId);
        if (err) {
            return err;
        }

        if (pageType === STPageType.slide) {
            // TODO addSlide kf 会携带错误 layoutId，前端清除错误数据
            if (source === MutationSource.Keyframe && this.data.slideOptions?.layoutId) {
                this.data.slideOptions.layoutId = '';
            }
            return Rule.every(
                source === MutationSource.Keyframe
                    ? () => undefined
                    : () => Rule.page.should.exist(presentation, parentId, STPageType.slideLayout),
                () => Rule.page.can.insertIntoSlideList(presentation, pageId, index),
            );
        }

        if (pageType === STPageType.slideLayout) {
            return Rule.page.can.insertIntoLayoutIdList(presentation, pageId, parentId, index);
        }

        if (pageType === STPageType.slideMaster) {
            return Rule.every(
                () => Rule.theme.should.existInPresentation(presentation, parentId),
                () => Rule.page.can.insertIntoMasterList(presentation, pageId, index),
            );
        }

        if (pageType === STPageType.notes) {
            return Rule.every(() => Rule.page.should.exist(presentation, parentId, STPageType.slide));
        }
    }

    private getParentIdFromData() {
        return (
            {
                [STPageType.slide]: this.data.slideOptions?.layoutId,
                [STPageType.slideLayout]: this.data.slideLayoutOptions?.masterId,
                [STPageType.slideMaster]: this.data.slideMasterOptions?.themeId,
                [STPageType.notes]: this.data.notesOptions?.slideId,
                [STPageType.notesMaster]: this.data.notesMasterOptions?.themeId,
                [STPageType.PBDEFAULT]: null,
            }[this.data.pageType] ?? ''
        );
    }
}
